import React, { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import Layout from './components/Layout'
import ScrollToTop from './components/ScrollToTop'
import Home from './pages/Home'
import Games from './pages/Games'
import Cases from './pages/Cases'
import CaseDetail from './pages/CaseDetail'
import CaseOpening from './pages/CaseOpening'
import CaseOpeningMulti from './pages/CaseOpeningMulti'
import Inventory from './pages/Inventory'
import Profile from './pages/Profile'
import { Payment } from './features/payment'
import TransactionHistory from './pages/TransactionHistory'
import Onboarding from './pages/Onboarding'
import Upgrader from './pages/Upgrader'
import Contract from './pages/Contract'
import Referrals from './pages/Referrals'
import ReferralTop from './pages/ReferralTop'
import Market from './pages/Market'
import Playground from './pages/Playground'
import Welcome from './pages/Welcome'
import { Battles, BattleCreate, BattlePage, BattleHistory } from './features/battle'
import { Royale, RoyaleHistory, RoyaleRound } from './features/royale'
import { Double, DoubleHistory, DoubleRound } from './features/double'
import { EventPage } from './features/event'
import OnboardingRouter from './components/OnboardingRouter'
import DeepLinkHandler from './components/DeepLinkHandler'
import DevTools from './components/DevTools'
import Loader from './components/Loader'
import useAppStore from './store/useAppStore'
import audioService from './services/audioService'
import './i18n/index.js'

const WelcomeRoute = () => {
  const { isAuthorized, isLoading } = useAppStore(state => ({
    isAuthorized: state.isAuthorized,
    isLoading: state.isLoading
  }))

  if (isLoading || isAuthorized === null) {
    return <Loader message="" />
  }

  if (isAuthorized) {
    return <Navigate to="/" replace />
  }

  return <Welcome />
}

const ProtectedRoute = () => {
  const { isAuthorized, isLoading } = useAppStore(state => ({
    isAuthorized: state.isAuthorized,
    isLoading: state.isLoading
  }))

  if (isLoading || isAuthorized === null) {
    return <Loader message="" />
  }

  if (!isAuthorized) {
    return <Navigate to="/welcome" replace />
  }

  return <Outlet />
}

const OnboardingWrapper = () => {
  return (
    <OnboardingRouter>
      <Outlet />
    </OnboardingRouter>
  )
}

function App() {
  const { i18n } = useTranslation()
  const { initializeApp } = useAppStore()

  useEffect(() => {
    // Предзагрузка изображений
    const preloadImage = (src, label) => {
      return new Promise(resolve => {
        const img = new Image()
        img.onload = () => {
          console.log(`✅ ${label} preloaded`)
          resolve()
        }
        img.onerror = () => {
          console.warn(`⚠️ Failed to preload ${label}`)
          resolve() // Не блокируем инициализацию при ошибке
        }
        img.src = src
      })
    }

    const preloadImages = () => {
      return Promise.all([
        preloadImage('/images/roulette-background.png', 'Roulette background'),
        preloadImage('/images/case-battle-bg.png', 'Case battle background'),
        preloadImage('/images/case-battle-winner.jpg', 'Case battle winner image')
      ])
    }

    // Инициализация приложения, предзагрузка звуков и изображений параллельно
    const initializeAll = async () => {
      try {
        // Запускаем все инициализации параллельно
        await Promise.all([
          initializeApp(i18n.changeLanguage),
          audioService.initialize(),
          preloadImages()
        ])
        console.log('✅ App, AudioService and Images initialized')
      } catch (error) {
        console.error('Failed to initialize app:', error)
      }
    }

    initializeAll()
  }, []) // Убираем initializeApp из зависимостей чтобы избежать повторных инициализаций

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      {/* Auto scroll to top on route changes */}
      <ScrollToTop />
      
      {/* Deep link handler for Telegram start_param */}
      <DeepLinkHandler>
        <div className="min-h-screen bg-dark-primary text-text-primary">
          {/* Animated Background */}
          <div className="animated-bg"></div>
          
          {/* Main App Content */}
          <div className="relative z-10">
            <Routes>
              {/* Welcome лендинг - публичный роут без Layout и без онбординг-редиректов */}
              <Route path="/welcome" element={<WelcomeRoute />} />

              <Route element={<ProtectedRoute />}>
                <Route element={<OnboardingWrapper />}>
                  {/* Онбординг - отдельный роут без Layout (без нижнего меню) */}
                  <Route path="/onboarding" element={<Onboarding />} />
                  
                  {/* Основные страницы с Layout */}
                  <Route path="/" element={<Layout />}>
                    <Route index element={<Home />} />
                    <Route path="games" element={<Games />} />
                    <Route path="cases" element={<Cases />} />
                    <Route path="cases/:id" element={<CaseDetail />} />
                    <Route path="cases/:id/opening" element={<CaseOpening />} />
                    <Route path="cases/:id/opening-multi" element={<CaseOpeningMulti />} />
                    <Route path="inventory" element={<Inventory />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="payment" element={<Payment />} />
                    <Route path="transactions" element={<TransactionHistory />} />
                    <Route path="upgrader" element={<Upgrader />} />
                    <Route path="contract" element={<Contract />} />
                    <Route path="referrals" element={<Referrals />} />
                    <Route path="referrals/top" element={<ReferralTop />} />
                    <Route path="market" element={<Market />} />
                    <Route path="playground" element={<Playground />} />
                    <Route path="event" element={<EventPage />} />
                    <Route path="battles" element={<Battles />} />
                    <Route path="battles/create" element={<BattleCreate />} />
                    <Route path="battles/history" element={<BattleHistory />} />
                    <Route path="battles/:id" element={<BattlePage />} />
                    <Route path="royale" element={<Royale />} />
                    <Route path="royale/history" element={<RoyaleHistory />} />
                    <Route path="royale/history/:id" element={<RoyaleRound />} />
                    <Route path="double" element={<Double />} />
                    <Route path="double/history" element={<DoubleHistory />} />
                    <Route path="double/history/:id" element={<DoubleRound />} />
                  </Route>
                </Route>
              </Route>
            </Routes>
          </div>
        </div>
      </DeepLinkHandler>
      
      {/* Toast Notifications */}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: 'linear-gradient(135deg, #00FF7F 0%, #4EFFC6 100%)',
            color: '#0A0A0F',
            borderRadius: '12px',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: '500',
          },
        }}
      />
      
      {/* Development Tools */}
      <DevTools />
    </Router>
  )
}

export default App
